import {useEffect, useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'
import {
  createReturn,
  getWarehouseOptions,
  getProductOptions,
  WarehouseOption,
  ProductOption,
} from './api'
import {getCurrentInventoryMetrics} from '../stock-management/api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {InventoryStateSummary} from '../inventory/components/InventoryStateSummary'
import {removeIndicatorSeparator, solidSelectStyles} from '../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const returnSchema = Yup.object().shape({
  return_type: Yup.string().required('Return type is required'),
  transaction_reference: Yup.string().required('Transaction reference is required'),
  return_date: Yup.string().required('Return date is required'),
  warehouse_id: Yup.number().min(1, 'Warehouse is required').required('Warehouse is required'),
  product_id: Yup.number().min(1, 'Product is required').required('Product is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  reason: Yup.string().required('Reason is required'),
  condition: Yup.string().required('Condition is required'),
  processed_by: Yup.string().required('Processed by is required'),
  action_required: Yup.string().required('Action required is required'),
  refund_amount: Yup.number().min(0),
  restock_fee: Yup.number().min(0),
  notes: Yup.string(),
})

// ─── Constants ────────────────────────────────────────────────────────────────

const RETURN_TYPES = ['Sales Return', 'Purchase Return', 'Quality Issue', 'Damage', 'Others']

const REASON_OPTIONS = [
  {value: 'defective_product', label: 'Defective Product'},
  {value: 'wrong_item_sent', label: 'Wrong Item Sent'},
  {value: 'customer_changed_mind', label: 'Customer Changed Mind'},
  {value: 'damage_in_transit', label: 'Damage in Transit'},
  {value: 'quality_issue', label: 'Quality Issue'},
  {value: 'better_price_found', label: 'Better Price Found'},
  {value: 'no_longer_needed', label: 'No Longer Needed'},
  {value: 'others', label: 'Others'},
]

const CONDITION_OPTIONS = [
  {value: 'New', label: 'New'},
  {value: 'Used', label: 'Used'},
  {value: 'Damaged', label: 'Damaged'},
  {value: 'Expired', label: 'Expired'},
  {value: 'Refurbished', label: 'Refurbished'},
  {value: 'Others', label: 'Others'},
]

const ACTION_OPTIONS = [
  {value: 'Repair', label: 'Repair'},
  {value: 'Replace', label: 'Replace'},
  {value: 'Refund', label: 'Refund'},
]

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReturnFormValues {
  return_type: string
  transaction_reference: string
  return_date: string
  warehouse_id: number
  product_id: number
  quantity: number
  reason: string
  condition: string
  refund_amount: number
  restock_fee: number
  action_required: string
  processed_by: string
  notes: string
}

type OptionType = {
  value: string | number
  label: string
}

const getTodayDate = () => new Date().toISOString().split('T')[0]

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateReturn: React.FC<{
  setCreateReturn: React.Dispatch<React.SetStateAction<boolean>>
  setShouldRefreshReturns: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setCreateReturn, setShouldRefreshReturns}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [warehouseOptions, setWarehouseOptions] = useState<OptionType[]>([])
  const [productOptions, setProductOptions] = useState<OptionType[]>([])
  const [warehouse, setWarehouse] = useState<OptionType>({label: 'Select Warehouse...', value: 0})
  const [product, setProduct] = useState<OptionType>({label: 'Select Product...', value: 0})
  const [reason, setReason] = useState<OptionType>({label: 'Select Reason...', value: ''})
  const [condition, setCondition] = useState<OptionType>({label: 'Select Condition...', value: ''})
  const [actionRequired, setActionRequired] = useState<OptionType>({
    label: 'Select Action...',
    value: '',
  })
  const [availableStock, setAvailableStock] = useState<number>(0)
  const [valuationPerUnit, setValuationPerUnit] = useState<number>(0)

  const initialValues: ReturnFormValues = {
    return_type: 'Sales Return',
    transaction_reference: '',
    return_date: getTodayDate(),
    warehouse_id: 0,
    product_id: 0,
    quantity: 0,
    reason: '',
    condition: '',
    refund_amount: 0,
    restock_fee: 0,
    action_required: '',
    processed_by: '',
    notes: '',
  }

  const formik = useFormik<ReturnFormValues>({
    initialValues,
    validationSchema: returnSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (values.return_type === 'Purchase Return' && values.quantity > availableStock) {
        toast.error('Return quantity exceeds available stock in the selected warehouse.')
        return
      }
      setLoading(true)
      const body = {
        process_return_id: null,
        company_id: company?.value,
        warehouse_id: values.warehouse_id,
        product_id: values.product_id,
        return_type: values.return_type.toLowerCase().replace(/ /g, '_'),
        transaction_reference: values.transaction_reference,
        return_date: values.return_date,
        quantity: values.quantity,
        reason: values.reason,
        condition: values.condition,
        refund_amount: values.refund_amount || 0,
        restock_fee: values.restock_fee || 0,
        action_required: values.action_required,
        processed_by: values.processed_by,
        notes: values.notes || '',
      }
      await createReturn(body)
        .then((res) => {
          res?.detail && toast.success('Return has been processed successfully.')
          formik.resetForm()
          setShouldRefreshReturns(true)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => {
          setLoading(false)
          setCreateReturn(false)
        })
    },
  })

  // Load warehouse and product options
  useEffect(() => {
    const loadOptions = async () => {
      if (!company?.value) return

      const [whRes, prodRes] = await Promise.all([
        getWarehouseOptions(company.value),
        getProductOptions(company.value),
      ])
      if (whRes) {
        setWarehouseOptions(
          whRes.map((w: WarehouseOption) => ({label: w.warehouse_name, value: w.warehouse_id}))
        )
      }
      if (prodRes) {
        setProductOptions(
          prodRes.map((p: ProductOption) => ({label: p.product_name, value: p.product_id}))
        )
      }
    }

    loadOptions()
  }, [company?.value])

  // Sync selects back into formik
  useEffect(() => {
    formik.setFieldValue('warehouse_id', warehouse.value, true)
    formik.setFieldValue('product_id', product.value, true)
    formik.setFieldValue('reason', reason.value, true)
    formik.setFieldValue('condition', String(condition.value).toLowerCase(), true)
    formik.setFieldValue('action_required', String(actionRequired.value).toLowerCase(), true)
  }, [warehouse.value, product.value, reason.value, condition.value, actionRequired.value])

  useEffect(() => {
    const loadMetrics = async () => {
      if (!company?.value || !warehouse.value || !product.value) {
        setAvailableStock(0)
        setValuationPerUnit(0)
        return
      }

      try {
        const metrics = await getCurrentInventoryMetrics(
          company.value,
          Number(warehouse.value),
          Number(product.value)
        )
        if (metrics) {
          setAvailableStock(Number(metrics.available_stock || 0))
          setValuationPerUnit(Number(metrics.valuation_per_unit || 0))
        }
      } catch {
        setAvailableStock(0)
        setValuationPerUnit(0)
      }
    }

    loadMetrics()
  }, [company?.value, warehouse.value, product.value])

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_return_form'
        aria-expanded='true'
        aria-controls='kt_return_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Process Return</h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div
          id='kt_return_form'
          className='collapse show d-flex justify-content-center flex-column flex-md-row'
        >
          {/* Left Column */}
          <div className='p-8 col pb-0 pb-md-4'>
            {/* Return Type */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Return Type</label>
              <div className='col-lg-9 fv-row d-flex flex-wrap gap-5 align-items-center pt-2'>
                {RETURN_TYPES.map((t) => (
                  <span key={t} className='form-check form-check-custom form-check-solid fs-6'>
                    {t}{' '}
                    <input
                      className='form-check-input'
                      type='radio'
                      name='return_type'
                      value={t}
                      checked={formik.values.return_type === t}
                      onChange={() => formik.setFieldValue('return_type', t)}
                      style={{marginLeft: '8px'}}
                    />
                  </span>
                ))}
              </div>
            </div>

            {/* Transaction Reference */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Transaction Ref
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Original Transaction Reference'
                  {...formik.getFieldProps('transaction_reference')}
                />
                {formik.touched.transaction_reference && formik.errors.transaction_reference && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.transaction_reference}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Return Date */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Return Date</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='date'
                  className='form-control form-control-lg form-control-solid'
                  {...formik.getFieldProps('return_date')}
                />
                {formik.touched.return_date && formik.errors.return_date && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.return_date}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Warehouse */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Warehouse</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={warehouse}
                  onChange={(option: any) => setWarehouse(option as OptionType)}
                  options={warehouseOptions}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Product */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Product</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={product}
                  onChange={(option: any) => setProduct(option as OptionType)}
                  options={productOptions}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <div className='col-lg-12'>
                <InventoryStateSummary
                  stockLabel='Current Stock'
                  availableStock={availableStock}
                  valuationPerUnit={valuationPerUnit}
                />
              </div>
            </div>

            {/* Quantity */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Quantity</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Return Quantity'
                  {...formik.getFieldProps('quantity')}
                />
                {formik.touched.quantity && formik.errors.quantity && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.quantity}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Reason</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={reason}
                  onChange={(option: any) => setReason(option as OptionType)}
                  options={REASON_OPTIONS}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className='p-8 col pt-0 pt-md-8 pb-md-4'>
            {/* Condition */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Condition</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={condition}
                  onChange={(option: any) => setCondition(option as OptionType)}
                  options={CONDITION_OPTIONS}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Action Required */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Action Required
              </label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={actionRequired}
                  onChange={(option: any) => setActionRequired(option as OptionType)}
                  options={ACTION_OPTIONS}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Refund Amount */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>
                Refund Amount (&#8377;)
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Refund Amount'
                  {...formik.getFieldProps('refund_amount')}
                />
                {formik.touched.refund_amount && formik.errors.refund_amount && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.refund_amount}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Restock Fee */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Restock Fee (&#8377;)</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Restocking Fee'
                  {...formik.getFieldProps('restock_fee')}
                />
                {formik.touched.restock_fee && formik.errors.restock_fee && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.restock_fee}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Processed By */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Processed By</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Processed By'
                  {...formik.getFieldProps('processed_by')}
                />
                {formik.touched.processed_by && formik.errors.processed_by && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.processed_by}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Notes</label>
              <div className='col-lg-9 fv-row'>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Notes'
                  rows={3}
                  {...formik.getFieldProps('notes')}
                />
                {formik.touched.notes && formik.errors.notes && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.notes}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setCreateReturn(false)
              setShouldRefreshReturns(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={
              loading ||
              !formik.isValid ||
              (formik.values.return_type === 'Purchase Return' &&
                formik.values.quantity > availableStock)
            }
          >
            {!loading ? 'Process Return' : null}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
